/**
 * @file OrderDocumentsTab.tsx
 * @description Документы заказа — счёт и УПД (phase10; фильтр по заказу —
 * phase19.24 этап 1, шаг 5.3).
 *
 * Список едет серверным фильтром `orderId`. До этого документы грузились по
 * `customerId` и фильтровались на клиенте — в заказе показывались счета
 * ДРУГИХ заказов того же клиента (находка §0 проекта фазы). Документы,
 * созданные до появления связи с заказом, останутся видны в карточке клиента,
 * но не здесь — это правильно.
 *
 * «Выставить счёт» показываем только заказу организации и только с правом
 * `SHOP_ISSUE_DOCUMENTS`: у розничного заказа счёта не бывает, а без права
 * сервер ответит 403. Кнопка контурная — главная кнопка вкладки одна
 * («Зафиксировать платёж», DS §5.3).
 *
 * phase19.31 (часть 3): строки — общим `DocumentCompactList` (как во вкладке
 * сделки и в реестре документов): «Счёт СЧТ-… · 1 175,00 ₽», пилюля статуса
 * по тону, вместо «RUB» — «₽»; состояния — общими `ListStates`.
 */

import { FilePlus2, FileText } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router";

import { OrderIssueDocumentDialog } from "./OrderIssueDocumentDialog";
import { useHasPermission } from "../../auth/hooks";
import { DocumentCompactList } from "../crm/documents/DocumentCompactList";
import { Button } from "../ui/button";
import { ListEmpty, ListError, ListSkeleton } from "../ui/ListStates";
import { SectionLabel } from "../ui/SectionLabel";
import { ApiError } from "../../lib/api-client";
import { listDocuments, type DocumentDto } from "../../lib/api-crm";
import type { OrderDto } from "../../lib/api-orders";
import { DOCUMENT_ISSUE_PERMISSION } from "../../lib/document-access";
import { useFocusedRow } from "../../hooks/useFocusedRow";
import { useOrderTabRevisit } from "../../hooks/useOrderTabRevisit";

interface Props {
  order: OrderDto;
  /**
   * id документа из `?focus=`: узлы «Счёт» и «УПД» в «Пути заказа» ведут во
   * вкладку «Оплаты и документы» и подсвечивают свою строку (§4) — то же
   * правило, что у платежей.
   */
  focusId?: string | null;
  /** Вкладка сейчас открыта: при возврате на неё список дочитывается тихо. */
  active?: boolean;
}

export function OrderDocumentsTab({ order, focusId, active = true }: Props) {
  const { slug } = useParams<{ slug: string }>();
  const canIssue = useHasPermission(DOCUMENT_ISSUE_PERMISSION);
  const focused = useFocusedRow(focusId);
  const [items, setItems] = useState<DocumentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await listDocuments({ orderId: order.id, limit: 100 });
      setItems(res.items);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить документы заказа");
    }
  }, [order.id]);

  useEffect(() => {
    void load();
  }, [load]);
  useOrderTabRevisit(active, load);

  // Счёт и УПД выставляют организации: у заказа физлица основания нет.
  const canIssueInvoice = canIssue && !!order.legalEntityId;

  const header = canIssueInvoice ? (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <SectionLabel as="h2">Документы</SectionLabel>
      <Button
        variant="outline"
        onClick={() => setIssuing(true)}
      >
        <FilePlus2 className="size-4" aria-hidden />
        Выставить счёт
      </Button>
    </div>
  ) : null;

  const dialog = canIssueInvoice ? (
    <OrderIssueDocumentDialog
      order={order}
      open={issuing}
      slug={slug}
      onClose={() => setIssuing(false)}
      onCreated={() => void load()}
    />
  ) : null;

  // Состояния — общими `ListStates`, строки — тем же списком, что у сделки
  // и в реестре документов (phase19.31, часть 3).
  const body =
    error && items === null ? (
      <ListError message={error} onRetry={() => void load()} />
    ) : items === null ? (
      <ListSkeleton rows={2} compact />
    ) : items.length === 0 ? (
      <ListEmpty
        filtered={false}
        spec={{
          icon: FileText,
          title: "Документов по заказу пока нет",
          description: canIssueInvoice ? "Счёт выставляется кнопкой «Выставить счёт»" : undefined,
        }}
      />
    ) : (
      <DocumentCompactList
        items={items}
        base={`/shop/${slug}`}
        highlightedId={focused.highlightedId}
        aria-label="Документы заказа"
      />
    );

  return (
    <div className="flex flex-col gap-3">
      {header}
      {body}
      {dialog}
    </div>
  );
}
